/**
 * German copy for the refusals the **employee-identification** surfaces can answer with — the employee setting
 * or changing their own critical step-up PIN, and the administrator enabling identification or clearing a PIN.
 *
 * Both surfaces used to render the server's `ApiError.description`: developer-facing English from
 * `ErrorCatalog`, kept for Postman, logs and docs (frontend style guide, *"Surfacing an API error — branch on
 * `code`, never render `description`"*). On the self-service panel that was a live leak — `E_PIN_TOO_WEAK` and
 * `E_CURRENT_PIN_INCORRECT` are the two things somebody actually hits while choosing a PIN, and both arrived
 * in English.
 *
 * ## Why the administrator's screen routes through here too
 *
 * `PUT /Company/Employee/SetEmployeeIdentification` and `PUT /Company/Employee/ResetPin` produce **no
 * structured code at all today**: their only failure inside the service is an unknown employee, answered with
 * a bare `NotFound()`, and the `[DynamicAuthorize]` gate above them answers a bare `ForbidResult`. Neither
 * carries an `ApiError`, so nothing there could ever have been mapped — but reading `description` was still
 * wrong, because it made those two screens leak English the moment the first code is added.
 *
 * That is exactly why they share this table rather than each keeping a private literal: when a code does
 * appear on either endpoint, there is one place it lands, and neither surface has to be rediscovered.
 *
 * **When a new code appears**, add it with its own sentence read from that code's XML doc (or, for
 * `E_CURRENT_PIN_INCORRECT`, the line comment above the registration, which is where the reasoning lives) in
 * `API.Core/Authorization/AuthErrorCodes.cs`. Do not widen {@link describeEmployeeIdentificationError} back to
 * `description`, and do not make the caller fallbacks more specific.
 */

import {readErrorCode} from '../../_auth/two-factor/second-factor-errors';

/**
 * `ApiError.code` → German sentence. All four come from `PUT /auth/employee/pin`, the self-service change.
 *
 * The wording of the first deliberately repeats the panel's own client-side hint word for word: the field
 * validator and the server's refusal describe one rule, and two spellings of it read as two different rules.
 */
const EMPLOYEE_IDENTIFICATION_MESSAGES: Readonly<Record<string, string>> = {
  /** Not six digits. Mirrors `PinExtensions.IsValidPin`, and the hint under the input. */
  E_PIN_INVALID_FORMAT:
    'Die PIN muss aus genau 6 Ziffern bestehen.',
  /**
   * Repeated, sequential or common. The English names the three shapes; so does this, with examples — being
   * told a PIN is "too easy to guess" without being told what counts is how somebody tries 111111 next.
   */
  E_PIN_TOO_WEAK:
    'Diese PIN ist zu leicht zu erraten. Vermeiden Sie Wiederholungen (111111), Zahlenfolgen (123456) und ' +
    'bekannte Muster.',
  /**
   * Identification was switched off for this account between the panel loading and the save. Nothing the
   * employee can do about it, so the sentence points at who can.
   */
  E_EMPLOYEE_IDENTIFICATION_NOT_ENABLED:
    'Die Mitarbeiteridentifikation ist für dieses Konto nicht freigeschaltet. Wenden Sie sich an Ihre ' +
    'Administration.',
  /**
   * The typed *current* PIN was wrong. Deliberately a 403 rather than a 401 on the server side, so that a
   * mistyped PIN does not trip the SPA's 401-means-log-out contract — which is why this sentence must read as
   * a retryable slip and nothing more.
   */
  E_CURRENT_PIN_INCORRECT:
    'Die aktuelle PIN ist falsch. Bitte versuchen Sie es erneut.'
};

/**
 * The German sentence for a failed employee-identification action.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a failure this table does not recognise. Per call site, because the
 *                 three actions are different acts — saving a PIN, clearing one, switching the feature on —
 *                 and it must stay a fixed German sentence: an unmapped code degrades to it, never to
 *                 `description`. On the administrator's two endpoints it is currently the *only* outcome, by
 *                 construction rather than by omission (see the module doc).
 */
export function describeEmployeeIdentificationError(err: unknown, fallback: string): string {
  const code = readErrorCode(err);
  return (code && EMPLOYEE_IDENTIFICATION_MESSAGES[code]) || fallback;
}
